import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionAPI, SubscriptionStatus } from '../services/api';

interface SubscriptionRouteProps {
  children: React.ReactNode;
  allowFreeAccess?: boolean;
}

const SubscriptionRoute: React.FC<SubscriptionRouteProps> = ({ children, allowFreeAccess = false }) => {
  const { user, isLoading } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!user) {
        setSubscriptionLoading(false);
        return;
      }

      try {
        const response = await subscriptionAPI.getStatus();
        setSubscriptionStatus(response);
      } catch (error: any) {
        console.error('🔐 SubscriptionRoute: Failed to check subscription status:', error);
        if (error.response) {
          console.error('🔐 SubscriptionRoute: Error response:', error.response.status, error.response.data);
        }
      } finally {
        setSubscriptionLoading(false);
      }
    };

    if (!isLoading) {
      checkSubscriptionStatus();
    }
  }, [user, isLoading]);

  // Show loading skeleton while checking authentication and subscription
  if (isLoading || subscriptionLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header Skeleton */}
        <div className="bg-white dark:bg-gray-800 shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                <div className="ml-3">
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mt-1 animate-pulse"></div>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 animate-pulse"></div>
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-16 animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Content Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-4"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 animate-pulse mb-2"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Admin users bypass subscription requirements
  if (user.is_admin) {
    return <>{children}</>;
  }

  if (allowFreeAccess) {
    return <>{children}</>;
  }

  // Redirect to subscription page if no active subscription
  if (!subscriptionStatus?.has_active_subscription) {
    return <Navigate to="/subscription" replace />;
  }

  // User is authenticated and has active subscription
  return <>{children}</>;
};

export default SubscriptionRoute;
